import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PasswordProtection from "@/components/PasswordProtection";
import { ChevronLeft, ChevronRight, FlaskConical, Handshake, Smartphone, MapPin, Map, Building2 } from "lucide-react";

// ─── Slide 0: Cover ─────────────────────────────────────────────
const SlideCover = () => (
  <div className="flex flex-col items-center justify-center h-full text-center px-8 laptop:px-24 desktop:px-32 bg-primary">
    <motion.h1
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.7 }}
      className="font-funnel font-bold text-7xl tablet:text-8xl laptop:text-9xl desktop:text-10xl text-white leading-[0.9] tracking-tight mb-4"
    >
      qwip
    </motion.h1>
    <motion.p
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6 }}
      className="font-funnel font-bold text-3xl tablet:text-4xl laptop:text-5xl text-white/90 mb-3"
    >
      all hands
    </motion.p>
    <motion.p
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.9 }}
      className="font-sans text-lg text-white/60"
    >
      30 March 2026
    </motion.p>
  </div>
);

// ─── Slide 1: Where we were this time last year ──────────────────
const SlideLastYear = () => (
  <div className="flex flex-col justify-start h-full px-8 laptop:px-20 desktop:px-28 pt-16 laptop:pt-24">
    <motion.h2
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.6 }}
      className="font-funnel font-bold text-4xl tablet:text-5xl laptop:text-6xl desktop:text-7xl text-foreground leading-tight tracking-tight"
    >
      where we were this time last year
    </motion.h2>
  </div>
);

// ─── Slide 2: Where we are today ─────────────────────────────────
const items2: { icon: typeof FlaskConical; text?: string; bullets?: string[] }[] = [
  { icon: FlaskConical, text: "Successfully trialled a working pod with real user(s). Captured key metrics." },
  { icon: Handshake, bullets: [
    "Secured a new pod supplier with asset financing agreements in place.",
    "Signed an agreement with Europe's largest shopping centre, owned by the largest commercial real estate company in Europe.",
    "Favourable 80/20 revenue share terms with a minimum guarantee.",
  ]},
  { icon: Smartphone, text: "An (almost) ready-to-release mobile app." },
];

const SlideToday = () => (
  <div className="flex flex-col justify-center h-full px-8 laptop:px-20 desktop:px-28 py-12">
    <motion.h2
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="font-funnel font-bold text-3xl tablet:text-4xl laptop:text-5xl desktop:text-6xl text-foreground leading-tight tracking-tight mb-10"
    >
      where we are today
    </motion.h2>
    <div className="max-w-4xl space-y-0">
      {items2.map((item, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 + i * 0.15 }}
          className={`flex items-start gap-4 py-5 ${i > 0 ? "border-t border-foreground/15" : ""}`}
        >
          <item.icon className="w-5 h-5 text-primary mt-0.5 shrink-0" />
          <div className="font-sans text-base laptop:text-lg text-foreground/80 leading-relaxed">
            {item.text && <p>{item.text}</p>}
            {item.bullets && (
              <ul className="list-disc list-inside space-y-1">
                {item.bullets.map((b, j) => <li key={j}>{b}</li>)}
              </ul>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

// ─── Slide 3: Where we will be in June ───────────────────────────
const items3 = [
  { icon: Building2, text: "A live pod in Westfield White City, accessible for people to use without our supervision." },
  { icon: Map, text: "A ready mobile app for people to find their favourite spots across London." },
  { icon: MapPin, text: "More locations lined up." },
];

const SlideJune = () => (
  <div className="flex flex-col justify-center h-full px-8 laptop:px-20 desktop:px-28 py-12">
    <motion.h2
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="font-funnel font-bold text-3xl tablet:text-4xl laptop:text-5xl desktop:text-6xl text-foreground leading-tight tracking-tight mb-10"
    >
      where we will be in June
    </motion.h2>
    <div className="max-w-4xl space-y-0">
      {items3.map((item, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 + i * 0.15 }}
          className={`flex items-start gap-4 py-5 ${i > 0 ? "border-t border-foreground/15" : ""}`}
        >
          <item.icon className="w-5 h-5 text-primary mt-0.5 shrink-0" />
          <p className="font-sans text-base laptop:text-lg text-foreground/80 leading-relaxed">
            {item.text}
          </p>
        </motion.div>
      ))}
    </div>
  </div>
);
// ─── Slide 4: Week by week priorities ────────────────────────────
function getWeeks(): { label: string; wc: string; key: string }[] {
  const start = new Date(2026, 2, 30); // 30 March 2026
  const end = new Date(2026, 5, 5); // 5 June 2026
  const weeks: { label: string; wc: string; key: string }[] = [];
  let weekNum = 1;
  const current = new Date(start);

  while (current <= end) {
    const day = current.getDate();
    const month = current.toLocaleString("en-GB", { month: "long" });
    weeks.push({
      label: `Week ${weekNum}`,
      wc: `w/c ${day} ${month}`,
      key: `allhands-week-${weekNum}`,
    });
    weekNum++;
    current.setDate(current.getDate() + 7);
  }
  return weeks;
}

const WEEKS = getWeeks();

const SlideWeeks = () => {
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    const saved: Record<string, string> = {};
    WEEKS.forEach((w) => {
      const v = localStorage.getItem(w.key);
      if (v) saved[w.key] = v;
    });
    setValues(saved);
  }, []);

  const handleChange = (key: string, val: string) => {
    setValues((prev) => ({ ...prev, [key]: val }));
    localStorage.setItem(key, val);
  };

  return (
    <div className="flex flex-col h-full px-8 laptop:px-20 desktop:px-28 py-10 overflow-y-auto">
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="font-funnel font-bold text-3xl tablet:text-4xl laptop:text-5xl desktop:text-6xl text-foreground leading-tight tracking-tight mb-8"
      >
        week by week priorities
      </motion.h2>
      <div className="max-w-3xl space-y-4">
        {WEEKS.map((w, i) => (
          <motion.div
            key={w.key}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 + i * 0.06 }}
            className="border border-foreground/10 rounded-2xl p-4"
          >
            <div className="flex items-baseline gap-3 mb-2">
              <span className="font-funnel font-bold text-lg text-foreground">{w.label}</span>
              <span className="font-sans text-sm text-muted-foreground">{w.wc}</span>
            </div>
            <textarea
              value={values[w.key] || ""}
              onChange={(e) => handleChange(w.key, e.target.value)}
              placeholder="Goals and priorities for this week…"
              className="w-full bg-foreground/[0.03] border border-foreground/10 rounded-xl px-4 py-3 font-sans text-sm text-foreground placeholder:text-muted-foreground/50 resize-none focus:outline-none focus:ring-1 focus:ring-foreground/20 min-h-[60px]"
              rows={2}
            />
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// ─── Slides Array ────────────────────────────────────────────────
const slides = [SlideCover, SlideLastYear, SlideToday, SlideJune, SlideWeeks];
const darkSlideIndices = new Set([0]);

// ─── AllHands Deck ───────────────────────────────────────────────
const AllHands = () => {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem("allhands_auth") === "true");
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);

  const goTo = useCallback(
    (index: number) => {
      if (index < 0 || index >= slides.length) return;
      setDirection(index > current ? 1 : -1);
      setCurrent(index);
    },
    [current]
  );

  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  // Keyboard
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); next(); }
      if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
      if (e.key === "f" || e.key === "F") {
        if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
        else document.exitFullscreen?.();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [next, prev]);

  // Touch/swipe
  useEffect(() => {
    let startX = 0;
    const handleTouchStart = (e: TouchEvent) => { startX = e.touches[0].clientX; };
    const handleTouchEnd = (e: TouchEvent) => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) diff > 0 ? next() : prev();
    };
    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchend", handleTouchEnd);
    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [next, prev]);

  if (!authed) {
    return <PasswordProtection password="podsforever42" onSuccess={() => { sessionStorage.setItem("allhands_auth", "true"); setAuthed(true); }} />;
  }

  const CurrentSlide = slides[current];
  const isDark = darkSlideIndices.has(current);
  const dotActive = isDark ? "bg-white" : "bg-foreground";
  const dotInactive = isDark ? "bg-white/30 hover:bg-white/50" : "bg-foreground/20 hover:bg-foreground/40";
  const arrowBg = isDark ? "bg-white/10 hover:bg-white/20" : "bg-foreground/10 hover:bg-foreground/20";
  const arrowIcon = isDark ? "text-white" : "text-foreground";

  const variants = {
    enter: { opacity: 0 },
    center: { opacity: 1 },
    exit: { opacity: 0 },
  };

  return (
    <div className={`h-screen w-screen overflow-hidden relative select-none ${isDark ? "bg-primary" : "bg-background"}`}>
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={current}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.6, ease: "easeInOut" }}
          className="absolute inset-0"
        >
          <CurrentSlide />
        </motion.div>
      </AnimatePresence>

      {/* Progress bar */}
      <div className={`absolute bottom-0 left-0 right-0 h-1 ${isDark ? "bg-white/10" : "bg-foreground/5"}`}>
        <motion.div
          className={`h-full ${isDark ? "bg-white" : "bg-foreground"}`}
          animate={{ width: `${((current + 1) / slides.length) * 100}%` }}
          transition={{ duration: 0.4 }}
        />
      </div>

      {/* Dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4">
        <div className="flex gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                i === current ? `${dotActive} w-6 shadow-md` : dotInactive
              }`}
            />
          ))}
        </div>
      </div>

      {/* Arrows */}
      {current > 0 && (
        <button
          onClick={prev}
          className={`absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full ${arrowBg} flex items-center justify-center transition-colors backdrop-blur-sm`}
        >
          <ChevronLeft className={`w-5 h-5 ${arrowIcon}`} />
        </button>
      )}
      {current < slides.length - 1 && (
        <button
          onClick={next}
          className={`absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full ${arrowBg} flex items-center justify-center transition-colors backdrop-blur-sm`}
        >
          <ChevronRight className={`w-5 h-5 ${arrowIcon}`} />
        </button>
      )}

      {/* Slide counter */}
      <div className="absolute top-6 right-6">
        <span className={`text-xs font-sans ${isDark ? "text-white/40" : "text-muted-foreground/50"}`}>
          {current + 1} / {slides.length}
        </span>
      </div>
    </div>
  );
};

export default AllHands;
